import { getUnSyncTransaction, exportUnSyncTransaction } from "apis/transaction.api";
import { DataTable, Icon } from "components/commons";
import { VenueContext, AppContext } from "contexts";
import { useApi, useFilter, useMount } from "hooks";
import { unSyncTransactionReportRequest } from "mappers/unsync-transaction-report.mapper";
import React, { useCallback, useContext, useState, useMemo } from "react";
import { mapObject, pluralize } from "services";
import { HeaderA } from "components/headers";
import { unSyncTransactionReportFilterState } from "./filters";
import UnsyncTransactionReportFilter from "./unsync-transaction-report-filter";
import { UnSyncTransactionReportColumns } from "./columns";
import lang from "translations";
import { ModuleWrapper } from "components/fragments";


const UnSyncTransactionReport = () => {
  const { venue } = useContext(VenueContext);
  const { setGlobalLoading } = useContext(AppContext);
  const {
    request: getUnSyncTransactionRequest,
    loading: loadingUnsyncTransactionReports,
    result: searchUnsyncTransactionReportResult = {
      data: { data: [] },
      metadata: { total: 0 }
    },
    error: errorUnsyncTransactionReports,
  } = useApi({
    api: getUnSyncTransaction,
    isArray: true,
  });


  const {
    request: getUnSyncTransactionRequestExport,
    loading: getUnSyncTransactionRequestExportLoading,
  } = useApi({
    api: exportUnSyncTransaction,
  });


  const { modifyFilter, modifyFilters, filterState, requestState, clearFilter, isFilterDirty } = useFilter(
    unSyncTransactionReportFilterState(venue)
  );

  const [hasAppliedFilter, setHasAppliedFilter] = useState(isFilterDirty);

  useMount(() => {
    fetchUnSyncTransactionReports(filterState, requestState);
  });

  const fetchUnSyncTransactionReports = useCallback(
    (filterState, requestState) => {
      setGlobalLoading(true);
      var dataToPass = mapObject({ ...requestState }, unSyncTransactionReportRequest);
      getUnSyncTransactionRequest(dataToPass);
      setGlobalLoading(false);
    },
    [getUnSyncTransactionRequest, setGlobalLoading]
  );

  const clearFilterCb = useCallback(
    (filterState, requestState) => {
      fetchUnSyncTransactionReports(filterState, requestState);
      setHasAppliedFilter(false);
    },
    [fetchUnSyncTransactionReports]
  );

  const applyFilterCb = useCallback(
    (searchKey) => {
      const { filterState, requestState } = modifyFilters({
        page: 1,
        searchKey,
      });
      fetchUnSyncTransactionReports(filterState, requestState);
      setHasAppliedFilter(true);
    },
    [fetchUnSyncTransactionReports, modifyFilters]
  );

  const sortCb = useCallback(
    ({ value, key }) => {
      const { filterState, requestState } = modifyFilters({ sort: { key, value } });
      fetchUnSyncTransactionReports(filterState, requestState);
    },
    [fetchUnSyncTransactionReports, modifyFilters]
  );

  const exportXlsx = useCallback(() => {
    setGlobalLoading(true);
    getUnSyncTransactionRequestExport(
      mapObject({ ...requestState }, unSyncTransactionReportRequest)
    );
    setGlobalLoading(false);
  }, [getUnSyncTransactionRequestExport, requestState, setGlobalLoading]);

  const noResult = useMemo(() => {
    return (
      <div className="pt-lg">
        <p className="text-xl text-gray-600">{lang.noReportsFound}</p>
        <p className="text-md text-gray-400">{lang.weDidNotFoundReports}</p>
      </div>
    );
  }, []);

  const transactionResultContent = useMemo(() => {
    
    if (hasAppliedFilter) {
      return `${searchUnsyncTransactionReportResult.metadata.total} ${pluralize(
        searchUnsyncTransactionReportResult.metadata.total,
        lang.searchResult,
        lang.searchResults
      )}`;
    }
    return null;
  }, [searchUnsyncTransactionReportResult.metadata.total, hasAppliedFilter]);

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.unsyncTransactionReports}
        button={{
          iconPrefix: <Icon className="mr-sm" name="download" paddingless fontSize={12} />,
          text: lang.exportXlsx,
          loading: getUnSyncTransactionRequestExportLoading,
          disabled: getUnSyncTransactionRequestExportLoading,
          onClick: () => {
            exportXlsx();
          },
        }}
        className="mb-md"
      />

      <UnsyncTransactionReportFilter
        filterState={filterState}
        requestState={requestState}
        modifyFilter={modifyFilter}
        clearFilter={clearFilter}
        onClear={clearFilterCb}
        onApply={applyFilterCb}
      />

      {!loadingUnsyncTransactionReports ? (
        <DataTable
          resultContent={transactionResultContent}
          page={filterState.page}
          pageSize={filterState.pageSize}
          onChangePage={modifyFilters}
          fetchList={fetchUnSyncTransactionReports}
          total={searchUnsyncTransactionReportResult?.metadata?.total || 0}
          loading={loadingUnsyncTransactionReports}
          columns={UnSyncTransactionReportColumns}
          data={searchUnsyncTransactionReportResult?.data || []}
          error={errorUnsyncTransactionReports}
          sort={filterState.sort}
          setSort={sortCb}
          hasAppliedFilter={isFilterDirty && hasAppliedFilter}
        />
      ) : (
        noResult
      )}
    </ModuleWrapper>
  );
};

export default UnSyncTransactionReport;
